// Lawn disease characteristics database for diagnosis feature

export interface DiseaseCharacteristics {
  id: string
  name: string
  category: 'fungal' | 'bacterial' | 'environmental'
  visualSymptoms: string[]
  affectedGrassTypes: string[]
  commonRegions: string[]
  severity: 'mild' | 'moderate' | 'severe'
  activeSeasons: string[]
  optimalConditions: { temperature: string; moisture: string; humidity?: string }
  treatmentOptions: { cultural: string[]; chemical: string[]; organic: string[] }
  preventionTips: string[]
}

export const DISEASE_CHARACTERISTICS: Record<string, DiseaseCharacteristics> = {
  'dollar-spot': {
    id: 'dollar-spot',
    name: 'Dollar Spot',
    category: 'fungal',
    visualSymptoms: [
      'Small, round, straw-colored patches 2-6 inches in diameter',
      'White cobweb-like mycelium visible in early morning dew',
      'Hourglass-shaped lesions on individual blades',
      'Patches may merge into larger irregular areas'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Southeast', 'Central'],
    severity: 'moderate',
    activeSeasons: ['spring', 'summer', 'fall'],
    optimalConditions: { temperature: '60-80°F', moisture: 'High humidity, low soil moisture', humidity: '> 60%' },
    treatmentOptions: {
      cultural: ['Increase nitrogen fertilization', 'Water deeply and infrequently in the morning', 'Reduce thatch buildup', 'Improve air circulation'],
      chemical: ['Propiconazole', 'Myclobutanil', 'Thiophanate-methyl', 'Boscalid'],
      organic: ['Neem oil application', 'Compost topdressing to boost microbial activity', 'Corn gluten meal for mild suppression']
    },
    preventionTips: ['Maintain adequate nitrogen levels', 'Avoid evening watering', 'Mow at proper height', 'Reduce thatch layer']
  },

  'brown-patch': {
    id: 'brown-patch',
    name: 'Brown Patch',
    category: 'fungal',
    visualSymptoms: [
      'Circular brown patches 1-3 feet in diameter',
      'Darker "smoke ring" border visible in morning dew',
      'Grass blades pull easily from the sheath',
      'Leaf lesions with tan centers and dark brown margins'
    ],
    affectedGrassTypes: ['Tall Fescue', 'Perennial Ryegrass', 'Kentucky Bluegrass', 'St. Augustinegrass'],
    commonRegions: ['Southeast', 'Mid-Atlantic', 'Central', 'Great Lakes'],
    severity: 'severe',
    activeSeasons: ['summer'],
    optimalConditions: { temperature: '80-95°F daytime, above 60°F at night', moisture: 'Prolonged leaf wetness', humidity: '> 90%' },
    treatmentOptions: {
      cultural: ['Reduce nitrogen in summer', 'Water early morning only', 'Improve drainage and air flow', 'Mow at proper height'],
      chemical: ['Azoxystrobin', 'Pyraclostrobin', 'Flutolanil', 'Propiconazole'],
      organic: ['Bacillus subtilis biological fungicide', 'Improve soil drainage', 'Reduce thatch']
    },
    preventionTips: ['Avoid excess nitrogen in hot weather', 'Water before 10 AM', 'Improve air circulation', 'Avoid mowing wet grass']
  },

  'rust': {
    id: 'rust',
    name: 'Rust',
    category: 'fungal',
    visualSymptoms: [
      'Orange, yellow, or brown powdery pustules on grass blades',
      'Powder rubs off on shoes and clothing',
      'Grass appears yellow-orange from a distance',
      'Individual blades show elongated pustules'
    ],
    affectedGrassTypes: ['Perennial Ryegrass', 'Kentucky Bluegrass', 'Tall Fescue', 'Zoysiagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Pacific Northwest'],
    severity: 'mild',
    activeSeasons: ['late summer', 'fall'],
    optimalConditions: { temperature: '68-85°F', moisture: 'Morning dew, overcast skies', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Increase nitrogen to promote growth', 'Mow frequently to remove infected blades', 'Water in the morning', 'Improve air circulation'],
      chemical: ['Propiconazole', 'Triadimefon', 'Myclobutanil'],
      organic: ['Neem oil', 'Increase mowing frequency', 'Boost nitrogen with organic fertilizer']
    },
    preventionTips: ['Maintain adequate fertility', 'Avoid drought stress', 'Choose rust-resistant cultivars', 'Mow regularly']
  },

  'red-thread': {
    id: 'red-thread',
    name: 'Red Thread',
    category: 'fungal',
    visualSymptoms: [
      'Pink to red thread-like strands extending from grass tips',
      'Irregular patches 4-8 inches wide with pink cast',
      'Red or pink antler-shaped structures on blade tips',
      'Affected areas appear water-soaked then turn tan'
    ],
    affectedGrassTypes: ['Perennial Ryegrass', 'Fine Fescue', 'Kentucky Bluegrass', 'Tall Fescue'],
    commonRegions: ['Northeast', 'Pacific Northwest', 'Great Lakes', 'Mid-Atlantic'],
    severity: 'mild',
    activeSeasons: ['spring', 'fall'],
    optimalConditions: { temperature: '60-75°F', moisture: 'High humidity, wet conditions', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Apply nitrogen fertilizer to promote recovery', 'Improve drainage', 'Avoid overwatering', 'Reduce compaction'],
      chemical: ['Propiconazole', 'Azoxystrobin', 'Chlorothalonil'],
      organic: ['Compost topdressing', 'Nitrogen-rich organic fertilizer', 'Improve soil biology']
    },
    preventionTips: ['Maintain adequate nitrogen', 'Avoid low fertility', 'Improve air circulation', 'Water in the morning']
  },

  'pythium-blight': {
    id: 'pythium-blight',
    name: 'Pythium Blight',
    category: 'fungal',
    visualSymptoms: [
      'Greasy, dark, water-soaked patches that appear overnight',
      'White cottony mycelium in early morning',
      'Patches follow drainage patterns or mower tracks',
      'Grass collapses and turns brown rapidly'
    ],
    affectedGrassTypes: ['Perennial Ryegrass', 'Kentucky Bluegrass', 'Tall Fescue', 'Bermudagrass'],
    commonRegions: ['Southeast', 'Mid-Atlantic', 'Central', 'Great Lakes'],
    severity: 'severe',
    activeSeasons: ['summer'],
    optimalConditions: { temperature: '85-95°F', moisture: 'Very wet, poor drainage', humidity: '> 90%' },
    treatmentOptions: {
      cultural: ['Improve drainage immediately', 'Reduce watering', 'Avoid mowing wet grass', 'Improve air circulation'],
      chemical: ['Mefenoxam', 'Fosetyl-Al', 'Propamocarb', 'Cyazofamid'],
      organic: ['Improve drainage', 'Reduce thatch', 'Avoid excessive watering']
    },
    preventionTips: ['Avoid overwatering in hot weather', 'Improve soil drainage', 'Reduce nitrogen in summer', 'Avoid mowing when wet']
  },

  'fusarium-patch': {
    id: 'fusarium-patch',
    name: 'Fusarium Patch (Microdochium Patch)',
    category: 'fungal',
    visualSymptoms: [
      'Small circular patches 1-3 inches, expanding to 12 inches',
      'Orange-brown to dark brown color',
      'White or pinkish mycelium at margins in wet weather',
      'Often appears at snow melt'
    ],
    affectedGrassTypes: ['Perennial Ryegrass', 'Fine Fescue', 'Kentucky Bluegrass', 'Tall Fescue'],
    commonRegions: ['Northeast', 'Pacific Northwest', 'Great Lakes', 'Upper Midwest'],
    severity: 'moderate',
    activeSeasons: ['fall', 'winter', 'early spring'],
    optimalConditions: { temperature: '32-60°F', moisture: 'Wet, cool conditions', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Reduce late fall nitrogen', 'Improve drainage', 'Avoid snow compaction', 'Reduce thatch'],
      chemical: ['Chlorothalonil', 'Propiconazole', 'Iprodione'],
      organic: ['Improve drainage', 'Reduce thatch buildup', 'Avoid late-season fertilizing']
    },
    preventionTips: ['Avoid excess nitrogen in fall', 'Prevent snow mold conditions', 'Improve air circulation', 'Reduce thatch']
  },

  'gray-snow-mold': {
    id: 'gray-snow-mold',
    name: 'Gray Snow Mold (Typhula Blight)',
    category: 'fungal',
    visualSymptoms: [
      'Circular straw-colored patches 6-24 inches after snow melt',
      'Gray-white mycelium matting grass together',
      'Small dark sclerotia (hard fungal bodies) on blades',
      'Grass appears matted and bleached'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Tall Fescue'],
    commonRegions: ['Northeast', 'Great Lakes', 'Upper Midwest', 'Pacific Northwest'],
    severity: 'moderate',
    activeSeasons: ['late winter', 'early spring'],
    optimalConditions: { temperature: '32-45°F', moisture: 'Under snow cover on unfrozen ground' },
    treatmentOptions: {
      cultural: ['Rake matted grass to improve air flow', 'Avoid late fall fertilization', 'Mow short before winter', 'Prevent snow piling'],
      chemical: ['Chlorothalonil applied in late fall', 'PCNB (Pentachloronitrobenzene)', 'Propiconazole'],
      organic: ['Rake and aerate in spring', 'Reduce thatch before winter', 'Avoid excessive fall nitrogen']
    },
    preventionTips: ['Mow until growth stops in fall', 'Avoid late nitrogen applications', 'Reduce thatch before winter', 'Spread snow piles in spring']
  },

  'pink-snow-mold': {
    id: 'pink-snow-mold',
    name: 'Pink Snow Mold (Fusarium Patch)',
    category: 'fungal',
    visualSymptoms: [
      'Circular patches 1-8 inches with pink to salmon-colored margins',
      'Pink or white mycelium on grass blades',
      'Can occur with or without snow cover',
      'Patches often have pink-tinged edges'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Tall Fescue'],
    commonRegions: ['Northeast', 'Great Lakes', 'Upper Midwest', 'Pacific Northwest'],
    severity: 'moderate',
    activeSeasons: ['late fall', 'winter', 'early spring'],
    optimalConditions: { temperature: '32-60°F', moisture: 'Wet, cool conditions with or without snow' },
    treatmentOptions: {
      cultural: ['Rake and promote air circulation', 'Avoid late fall nitrogen', 'Improve drainage', 'Mow before winter'],
      chemical: ['Chlorothalonil', 'Iprodione', 'Propiconazole'],
      organic: ['Rake affected areas', 'Topdress with compost', 'Improve soil biology']
    },
    preventionTips: ['Reduce nitrogen in late fall', 'Improve drainage', 'Mow until dormancy', 'Avoid leaf litter accumulation']
  },

  'fairy-ring': {
    id: 'fairy-ring',
    name: 'Fairy Ring',
    category: 'fungal',
    visualSymptoms: [
      'Dark green circles or arcs of stimulated grass growth',
      'Mushrooms or puffballs may appear in rings',
      'Dead or thin grass zone adjacent to dark green ring',
      'Hydrophobic soil layer in affected zone'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Bermudagrass', 'Zoysiagrass', 'Fine Fescue'],
    commonRegions: ['All regions'],
    severity: 'moderate',
    activeSeasons: ['spring', 'summer', 'fall'],
    optimalConditions: { temperature: '60-80°F', moisture: 'Moderate moisture with organic debris in soil' },
    treatmentOptions: {
      cultural: ['Core aerate through the ring', 'Apply wetting agent to hydrophobic soil', 'Deep water to break through', 'Remove mushrooms when they appear'],
      chemical: ['Flutolanil (limited effectiveness)', 'Azoxystrobin (suppression only)', 'Soil fumigation for severe cases'],
      organic: ['Core aeration with wetting agents', 'Remove buried organic debris if possible', 'Topdress with compost']
    },
    preventionTips: ['Remove buried wood and organic debris', 'Core aerate regularly', 'Maintain consistent watering', 'Use wetting agents preventively']
  },

  'leaf-spot': {
    id: 'leaf-spot',
    name: 'Leaf Spot (Bipolaris/Drechslera)',
    category: 'fungal',
    visualSymptoms: [
      'Dark brown to purple-black spots on grass blades',
      'Spots have tan centers with dark borders',
      'Extensive infection causes "melting out" of turf',
      'Leaf sheaths may show lesions'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Bermudagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Central'],
    severity: 'moderate',
    activeSeasons: ['spring', 'early summer'],
    optimalConditions: { temperature: '60-80°F', moisture: 'Frequent rain or irrigation', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Raise mowing height', 'Reduce watering frequency', 'Avoid evening irrigation', 'Improve air circulation'],
      chemical: ['Chlorothalonil', 'Propiconazole', 'Iprodione', 'Mancozeb'],
      organic: ['Raise mowing height', 'Improve air flow', 'Apply compost tea']
    },
    preventionTips: ['Mow at proper height', 'Water in the morning', 'Avoid excess nitrogen in spring', 'Choose resistant cultivars']
  },

  'necrotic-ring-spot': {
    id: 'necrotic-ring-spot',
    name: 'Necrotic Ring Spot',
    category: 'fungal',
    visualSymptoms: [
      'Ring-shaped patches 6-12 inches in diameter',
      'Green grass in center of ring ("frog-eye" pattern)',
      'Straw-colored dead grass forming the ring',
      'Root system is severely damaged'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Fine Fescue'],
    commonRegions: ['Northeast', 'Great Lakes', 'Upper Midwest', 'Pacific Northwest'],
    severity: 'severe',
    activeSeasons: ['late spring', 'summer', 'fall'],
    optimalConditions: { temperature: '65-82°F', moisture: 'Wet spring followed by heat stress' },
    treatmentOptions: {
      cultural: ['Deep watering during stress', 'Core aerate to reduce compaction', 'Overseed with resistant varieties', 'Reduce thatch'],
      chemical: ['Thiophanate-methyl (drench application)', 'Fenarimol', 'Azoxystrobin'],
      organic: ['Core aeration', 'Compost topdressing', 'Overseed with perennial ryegrass or tall fescue']
    },
    preventionTips: ['Avoid compaction', 'Maintain deep root system', 'Core aerate annually', 'Choose resistant bluegrass cultivars']
  },

  'summer-patch': {
    id: 'summer-patch',
    name: 'Summer Patch',
    category: 'fungal',
    visualSymptoms: [
      'Circular to crescent-shaped wilted patches',
      'Patches turn straw-colored, 6 inches to 3 feet across',
      'Often "frog-eye" pattern with green center',
      'Appears during hottest part of summer'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Fine Fescue', 'Perennial Ryegrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Central'],
    severity: 'severe',
    activeSeasons: ['summer'],
    optimalConditions: { temperature: '85-95°F', moisture: 'Wet spring followed by hot dry summer' },
    treatmentOptions: {
      cultural: ['Raise mowing height', 'Deep, infrequent watering', 'Core aerate to reduce compaction', 'Overseed with tall fescue'],
      chemical: ['Azoxystrobin (preventive in spring)', 'Propiconazole', 'Thiophanate-methyl'],
      organic: ['Core aeration', 'Compost topdressing', 'Overseed with resistant species']
    },
    preventionTips: ['Apply preventive fungicide in late spring', 'Avoid compaction', 'Maintain deep roots', 'Improve drainage']
  },

  'take-all-patch': {
    id: 'take-all-patch',
    name: 'Take-All Patch',
    category: 'fungal',
    visualSymptoms: [
      'Irregular bronze or bleached patches',
      'Dark runner hyphae on roots and stolons',
      'Weeds invade thinned areas',
      'Patches expand over multiple seasons'
    ],
    affectedGrassTypes: ['Bermudagrass', 'St. Augustinegrass', 'Zoysiagrass', 'Centipede'],
    commonRegions: ['Southeast', 'South Central', 'Gulf Coast'],
    severity: 'severe',
    activeSeasons: ['fall', 'winter', 'spring'],
    optimalConditions: { temperature: '60-75°F', moisture: 'Wet, poorly drained soils', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Lower soil pH with sulfur', 'Improve drainage', 'Apply manganese', 'Reduce alkaline amendments'],
      chemical: ['Azoxystrobin', 'Fenarimol', 'Myclobutanil'],
      organic: ['Acidify soil with elemental sulfur', 'Apply sphagnum peat topdressing', 'Improve soil drainage']
    },
    preventionTips: ['Monitor soil pH (keep below 6.5)', 'Avoid liming affected areas', 'Improve soil drainage', 'Apply preventive fungicide in fall']
  },

  'anthracnose': {
    id: 'anthracnose',
    name: 'Anthracnose',
    category: 'fungal',
    visualSymptoms: [
      'Irregular yellow to brown patches',
      'Black acervuli (tiny dark fruiting bodies) on stems and leaves',
      'Yellowing of individual plants within the turf',
      'Basal rot may cause plants to pull easily from soil'
    ],
    affectedGrassTypes: ['Perennial Ryegrass', 'Kentucky Bluegrass', 'Tall Fescue', 'Bermudagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Southeast'],
    severity: 'moderate',
    activeSeasons: ['summer'],
    optimalConditions: { temperature: '78-90°F', moisture: 'Wet leaves, compacted soil', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Improve drainage', 'Reduce compaction', 'Adequate nitrogen', 'Raise mowing height'],
      chemical: ['Chlorothalonil', 'Azoxystrobin', 'Fludioxonil', 'Thiophanate-methyl'],
      organic: ['Reduce compaction through aeration', 'Improve soil health', 'Apply compost']
    },
    preventionTips: ['Avoid low mowing heights', 'Maintain adequate fertility', 'Reduce compaction', 'Water in the morning']
  },

  'ascochyta-leaf-blight': {
    id: 'ascochyta-leaf-blight',
    name: 'Ascochyta Leaf Blight',
    category: 'fungal',
    visualSymptoms: [
      'Sudden widespread browning of leaf tips',
      'Large straw-colored areas appearing overnight',
      'Grass tips die back uniformly across large areas',
      'Rapid onset after rain events'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Perennial Ryegrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Central'],
    severity: 'mild',
    activeSeasons: ['spring', 'early summer'],
    optimalConditions: { temperature: '70-85°F', moisture: 'Rapid dry/wet cycles' },
    treatmentOptions: {
      cultural: ['Mow at proper height', 'Water consistently', 'Avoid drought stress', 'Sharpen mower blades'],
      chemical: ['Generally not recommended; grass recovers on its own', 'Chlorothalonil for severe cases'],
      organic: ['Maintain consistent moisture', 'Apply compost topdressing', 'Avoid stressing the lawn']
    },
    preventionTips: ['Maintain consistent watering schedule', 'Sharpen mower blades', 'Avoid drought stress cycles', 'Mow at proper height']
  },

  'powdery-mildew': {
    id: 'powdery-mildew',
    name: 'Powdery Mildew',
    category: 'fungal',
    visualSymptoms: [
      'White to gray powdery coating on grass blades',
      'Affected areas appear dusted with flour',
      'Common in shaded areas with poor air circulation',
      'Leaves may yellow and thin under heavy infection'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Fine Fescue', 'Bermudagrass'],
    commonRegions: ['Northeast', 'Mid-Atlantic', 'Great Lakes', 'Pacific Northwest'],
    severity: 'mild',
    activeSeasons: ['spring', 'fall'],
    optimalConditions: { temperature: '60-72°F', moisture: 'Moderate humidity, shade', humidity: '60-80%' },
    treatmentOptions: {
      cultural: ['Improve air circulation', 'Reduce shade if possible', 'Increase sun exposure', 'Avoid overwatering'],
      chemical: ['Propiconazole', 'Myclobutanil', 'Triadimefon'],
      organic: ['Improve air circulation', 'Reduce shade', 'Plant shade-tolerant species']
    },
    preventionTips: ['Plant shade-tolerant grass varieties', 'Prune overhanging trees', 'Improve air circulation', 'Avoid overwatering shaded areas']
  },

  'slime-mold': {
    id: 'slime-mold',
    name: 'Slime Mold',
    category: 'fungal',
    visualSymptoms: [
      'Gray, black, yellow, or white crusty growth on grass blades',
      'Appears as powdery or slimy patches on leaf surfaces',
      'Does not infect grass; just grows on the surface',
      'May cover large areas after wet weather'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Tall Fescue', 'Bermudagrass', 'Zoysiagrass', 'St. Augustinegrass'],
    commonRegions: ['All regions'],
    severity: 'mild',
    activeSeasons: ['spring', 'summer'],
    optimalConditions: { temperature: '65-85°F', moisture: 'After heavy rain or prolonged wet periods' },
    treatmentOptions: {
      cultural: ['Mow or rake to break up the growth', 'Hose off with strong water spray', 'Improve air circulation'],
      chemical: ['No chemical treatment needed'],
      organic: ['Simply mow or wash off', 'Will disappear on its own when it dries']
    },
    preventionTips: ['Not harmful; cosmetic issue only', 'Improve drainage if recurring', 'Reduce thatch', 'Improve air circulation']
  },

  'yellow-patch': {
    id: 'yellow-patch',
    name: 'Yellow Patch (Cool-Season Brown Patch)',
    category: 'fungal',
    visualSymptoms: [
      'Yellow to light brown rings or patches',
      'Ring patterns 6 inches to 3 feet across',
      'Often a yellow or light green ring border',
      'Appears in cool, wet weather'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Fine Fescue', 'Tall Fescue'],
    commonRegions: ['Northeast', 'Great Lakes', 'Pacific Northwest', 'Upper Midwest'],
    severity: 'mild',
    activeSeasons: ['fall', 'winter', 'early spring'],
    optimalConditions: { temperature: '40-60°F', moisture: 'Wet, overcast conditions' },
    treatmentOptions: {
      cultural: ['Reduce nitrogen in fall', 'Improve drainage', 'Avoid overwatering', 'Improve air circulation'],
      chemical: ['Propiconazole', 'Azoxystrobin'],
      organic: ['Improve drainage', 'Reduce thatch', 'Avoid excessive fall fertilization']
    },
    preventionTips: ['Avoid heavy nitrogen in fall', 'Improve drainage', 'Reduce thatch buildup', 'Water in the morning']
  },

  'stripe-smut': {
    id: 'stripe-smut',
    name: 'Stripe Smut',
    category: 'fungal',
    visualSymptoms: [
      'Long black stripes running parallel along grass blades',
      'Blades curl and twist as they dry',
      'Black sooty spore masses released from cracked stripes',
      'Affected plants thin and die over time'
    ],
    affectedGrassTypes: ['Kentucky Bluegrass', 'Perennial Ryegrass', 'Bermudagrass'],
    commonRegions: ['Northeast', 'Great Lakes', 'Upper Midwest'],
    severity: 'moderate',
    activeSeasons: ['spring', 'fall'],
    optimalConditions: { temperature: '50-70°F', moisture: 'Cool, moist weather activates dormant infections' },
    treatmentOptions: {
      cultural: ['Overseed with resistant cultivars', 'Maintain adequate nitrogen', 'Mow at proper height', 'Remove clippings from infected areas'],
      chemical: ['Propiconazole (fall application)', 'Triadimefon'],
      organic: ['Overseed with resistant varieties', 'Improve overall turf health', 'Compost topdressing']
    },
    preventionTips: ['Plant smut-resistant cultivars', 'Maintain adequate fertility', 'Avoid drought stress', 'Overseed with resistant seed blends']
  },

  'spring-dead-spot': {
    id: 'spring-dead-spot',
    name: 'Spring Dead Spot',
    category: 'fungal',
    visualSymptoms: [
      'Circular dead patches 6 inches to 3 feet across visible at spring green-up',
      'Bermudagrass fails to green up in affected areas',
      'Roots and stolons are dark and rotted',
      'Patches recur and enlarge each year'
    ],
    affectedGrassTypes: ['Bermudagrass', 'Zoysiagrass'],
    commonRegions: ['Southeast', 'South Central', 'Mid-Atlantic'],
    severity: 'severe',
    activeSeasons: ['spring'],
    optimalConditions: { temperature: 'Fungus active in fall at 50-70°F; damage visible in spring', moisture: 'Wet fall and winter conditions' },
    treatmentOptions: {
      cultural: ['Improve drainage', 'Reduce thatch', 'Avoid excessive nitrogen in fall', 'Lower soil pH'],
      chemical: ['Tebuconazole (applied in fall)', 'Fenarimol', 'Azoxystrobin (fall preventive)'],
      organic: ['Improve drainage', 'Core aerate in summer', 'Apply acidifying amendments']
    },
    preventionTips: ['Apply preventive fungicide in early fall', 'Avoid late fall nitrogen', 'Maintain proper soil pH', 'Reduce thatch and compaction']
  },

  'large-patch': {
    id: 'large-patch',
    name: 'Large Patch',
    category: 'fungal',
    visualSymptoms: [
      'Large circular patches several feet in diameter',
      'Orange-yellow borders at active margins',
      'Grass at patch edges pulls easily from stolons',
      'Center may recover while edges expand'
    ],
    affectedGrassTypes: ['Zoysiagrass', 'St. Augustinegrass', 'Bermudagrass', 'Centipede'],
    commonRegions: ['Southeast', 'South Central', 'Gulf Coast'],
    severity: 'severe',
    activeSeasons: ['fall', 'spring'],
    optimalConditions: { temperature: '50-70°F', moisture: 'Wet conditions, especially during dormancy transition', humidity: '> 80%' },
    treatmentOptions: {
      cultural: ['Reduce nitrogen in fall', 'Improve drainage', 'Reduce thatch', 'Avoid overwatering'],
      chemical: ['Azoxystrobin (fall preventive)', 'Propiconazole', 'Flutolanil'],
      organic: ['Improve drainage', 'Reduce thatch', 'Avoid excessive irrigation']
    },
    preventionTips: ['Apply preventive fungicide in early fall', 'Avoid excess nitrogen before dormancy', 'Improve air circulation', 'Reduce thatch']
  }
}

// Helper: get disease by ID
export function getDiseaseById(id: string): DiseaseCharacteristics | undefined {
  return DISEASE_CHARACTERISTICS[id]
}

// Helper: find disease by name (case-insensitive partial match)
export function findDiseaseByName(name: string): DiseaseCharacteristics | undefined {
  const lower = name.toLowerCase()
  return Object.values(DISEASE_CHARACTERISTICS).find(d =>
    d.name.toLowerCase().includes(lower) || d.id.includes(lower.replace(/\s+/g, '-'))
  )
}

// Helper: get diseases by grass type
export function getDiseasesByGrassType(grassType: string): DiseaseCharacteristics[] {
  return Object.values(DISEASE_CHARACTERISTICS).filter(d =>
    d.affectedGrassTypes.some(g => g.toLowerCase().includes(grassType.toLowerCase()))
  )
}

// Helper: get diseases by severity
export function getDiseasesBySeverity(severity: 'mild' | 'moderate' | 'severe'): DiseaseCharacteristics[] {
  return Object.values(DISEASE_CHARACTERISTICS).filter(d => d.severity === severity)
}
